<template>
  <div class="sidebar-container">
    <el-menu
      :default-active="activeMenu"
      class="sidebar-menu"
      background-color="#001529"
      text-color="#fff"
      active-text-color="#ffd04b"
      :collapse="isCollapse"
      @select="handleMenuSelect"
    >
      <!-- 学生菜单 -->
      <template v-if="isStudent">
        <!-- 学生端保留首页 -->
        <el-menu-item index="/">
          <span slot="title">首页</span>
        </el-menu-item>
    
        <el-sub-menu index="personal-center">
          <template v-slot:title>
            <span>个人中心</span>
          </template>
          <el-menu-item index="/student/profile/info">个人信息</el-menu-item>
          <el-menu-item index="/student/profile/videos">指导视频</el-menu-item>
          <el-menu-item index="/student/profile/makeup">体测补考</el-menu-item>
        </el-sub-menu>
        
        <el-sub-menu index="health-records">
          <template v-slot:title>
            <span>健康档案</span>
          </template>
          <el-menu-item index="/student/records">体测成绩</el-menu-item>
          <el-menu-item index="/student/health">健康分析</el-menu-item>
          <el-menu-item index="/student/health-advice">健康建议</el-menu-item>
          <!-- 健康追踪模块已移除 -->
        </el-sub-menu>
        
        <el-menu-item index="/student/message-board">
          <span slot="title">留言板</span>
        </el-menu-item>
        
        <el-menu-item index="/logout" @click="handleLogout">
          <span>退出登录</span>
        </el-menu-item>
      </template>

      <!-- 教师菜单 -->
      <template v-if="isTeacher">
        <!-- 教师端删除首页 -->
        <el-menu-item index="/teacher/profile">
          <span slot="title">个人中心</span>
        </el-menu-item>
        
        <el-sub-menu index="health-records">
          <template v-slot:title>
            <span>学生健康档案</span>
          </template>
          <el-menu-item index="/teacher/fitness-scores">体测成绩管理</el-menu-item>
          <el-menu-item index="/teacher/class-health">班级总体健康情况</el-menu-item>
        </el-sub-menu>
        
        <el-sub-menu index="teacher">
          <template v-slot:title>
            <span>班级管理</span>
          </template>
          <el-menu-item index="/teacher/students">学生管理</el-menu-item>
          <el-menu-item index="/teacher/makeup">补考审批</el-menu-item>
        </el-sub-menu>
        
        <!-- 添加留言板菜单 -->
        <el-menu-item index="/teacher/message-board">
          <span slot="title">留言板</span>
        </el-menu-item>
        
        <el-menu-item index="/logout" @click="handleLogout">
          <span>退出登录</span>
        </el-menu-item>
      </template>

      <!-- 管理员菜单 -->
      <template v-if="isAdmin">
        <!-- 直接显示所有菜单项，不使用嵌套子菜单 -->
        <el-menu-item index="/admin/activities">体测活动管理</el-menu-item>
        <el-menu-item index="/admin/test-items">体测项目管理</el-menu-item>
        <el-menu-item index="/admin/message-board">留言板管理</el-menu-item>
        <el-menu-item index="/admin/information">体育资讯管理</el-menu-item>
        <el-menu-item index="/admin/users">基础用户管理</el-menu-item>
        <el-menu-item index="/admin/makeup-exams">补考管理</el-menu-item>
        <el-menu-item index="/admin/score-management">成绩管理</el-menu-item>
        <el-menu-item index="/logout" @click="handleLogout">退出登录</el-menu-item>
      </template>
    </el-menu>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../../store/modules/auth';
import {
  // 所有图标已移除
} from '@element-plus/icons-vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// 侧边栏折叠状态
const isCollapse = ref(false);

// 当前激活菜单
const activeMenu = computed(() => route.path);

// 用户角色判断
const isStudent = computed(() => authStore.isStudent());
const isTeacher = computed(() => authStore.isTeacher());
const isAdmin = computed(() => authStore.isAdmin());

// 菜单选择处理
const handleMenuSelect = (index) => {
  // 跳过退出登录菜单项的路由跳转
  if (index !== '/logout') {
    router.push(index);
  }
};

// 监听路由变化，更新激活菜单
watch(
  () => route.path,
  (newPath) => {
    activeMenu.value = newPath;
  }
);

// 处理退出登录
const handleLogout = () => {
  // 先清除认证数据
  authStore.logout();
  // 移除所有localStorage项，确保彻底清除
  localStorage.removeItem('token');
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('userInfo');
  // 使用replace跳转，避免历史记录问题并防止组件继续加载
  router.replace('/login');
  // 强制刷新页面，确保不再显示任何未登录提示
  setTimeout(() => {
    window.location.reload();
  }, 100);
};
</script>

<style scoped>
.sidebar-container {
  width: 200px;
  height: calc(100vh - 10px);
  position: fixed;
  left: 0;
  top: 10px;
  z-index: 10;
  background-color: #fff;
  box-shadow: 1px 0 3px rgba(0, 0, 0, 0.05);
}

.sidebar-menu {
  height: 100%;
  border-right: none;
  box-sizing: border-box;
}

:deep(.el-menu) {
  border-right: none !important;
  padding: 0;
}

:deep(.el-menu-item),
:deep(.el-submenu__title) {
  height: 60px;
  line-height: 60px;
  margin: 0 4px;
  border-radius: 4px;
  padding: 0 20px !important;
  text-align: center;
}

/* 确保教师端子菜单项与主菜单项对齐 */
:deep(.el-sub-menu.is-opened .el-menu-item) {
  padding: 0 20px !important;
  text-align: center;
}

:deep(.el-menu-item:hover),
:deep(.el-submenu__title:hover) {
  background-color: #f5f5f5;
}

:deep(.el-menu--collapse .el-submenu__title),
:deep(.el-menu--collapse .el-menu-item) {
  padding: 0 20px !important;
}

/* 确保子菜单项与主菜单项对齐 */
:deep(.el-sub-menu .el-menu-item) {
  padding: 0 20px !important;
  text-align: center;
}

/* 确保嵌套子菜单项的对齐 */
:deep(.el-sub-menu .el-sub-menu .el-menu-item) {
  padding: 0 20px !important;
  text-align: center;
}

/* 调整学生端子菜单的对齐，使其与主菜单文本对齐 */
:deep(.el-submenu__content .el-menu-item) {
  padding-left: 20px !important;
  text-align: center;
}
</style>